import { AsyncPipe, NgClass } from "@angular/common";
import { SafeResourceUrl } from "@angular/platform-browser";
import { TranslateService, TranslatePipe } from "@ngx-translate/core";
import { Component, ElementRef, OnDestroy, OnInit, inject, viewChild, input, output } from "@angular/core";
import { select, Store } from "@ngrx/store";
import { forkJoin, Observable, ReplaySubject, Subscription } from "rxjs";
import { filter, take } from "rxjs/operators";
import {
	faSignInAlt,
	faSpinner,
	faTimes,
	faSync,
	faCheckToSlot,
	faQuestionCircle
} from "@fortawesome/pro-light-svg-icons";
import { MatButton } from "@angular/material/button";
import { ClipboardModule } from "ngx-clipboard";
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";

import { FeatureFlag } from "libs/feature-flags/src/lib/models/feature-flags.model";
import { FeatureFlagsService } from "libs/feature-flags/src/lib/services/feature-flags.lib.service";
import {
	FUNCTIONAL_PRODUCT_KEYS,
	ProductInfoForKey,
	ProductKey,
} from "libs/product/src/lib/models/product.model";
import { TreatmentBaseService } from "libs/treatments/src/lib/services";
import { selectTreatmentFromUrl, TreatmentState } from "libs/treatments/src/lib/store";
import { TreatmentModel, Webviewer } from "libs/treatments/src/lib/models/treatment.model";
import { ReleaseData } from "libs/anamnese/src/lib/models/anamnese.model";
import { AnamnesisService } from "libs/anamnese/src/lib/services/anamnesis.lib.service";
import { MediaBaseService } from "libs/media/src/lib/services/media-base.lib.service";
import {
	CA_WEBVIEWER_APP_ORIGIN,
	isWebviewerMessage,
	WebViewerApiCommand,
	WebViewerApiCommandCancelModification,
	WebViewerApiCommandEnterModificationMode,
	WebViewerApiCommandLoadModification,
	WebViewerApiCommandSubmitModification,
	WebViewerMessageInitialized,
	WebViewerMessageLoadingFinished,
	WebViewerMessageType,
	WebViewerMessageSubmitModification,
} from "libs/web-viewer/src/lib/models/web-viewer-app.model";
import {
	WebviewerCreatedByRole,
	WebViewerDataContainerType,
	WebViewerModificationDataContainer,
	WebViewerModificationData,
} from "libs/web-viewer/src/lib/models/webviewer-data.model";
import { ClientName } from 'libs/config/client.names';
import { NotificationService } from 'libs/tm-common/src/lib/services';
import { WebviewerDocument } from 'libs/web-viewer/src/lib/models/webviewer-tms-api.model';
import { WebviewerModificationService } from 'libs/web-viewer/src/lib/services/webviewer-modification.lib.service';
import { APP_CONFIG_TOKEN, AppConfig } from "libs/config/app.lib.config";
import { WebviewerAnnotationHelpDialogComponent } from "../webviewer-annotation-help-dialog/webviewer-annotation-help-dialog.component";

@Component({
	selector: 'lib-ca-webviewer',
	templateUrl: './ca-webviewer.component.html',
	styleUrls: ['./ca-webviewer.component.scss'],
	host: {
		'[class.fill-height]': 'fillHeight()',
	},
	imports: [
		MatButton,
		ClipboardModule,
		FaIconComponent,
		TranslatePipe,
		AsyncPipe,
		NgClass,
		MatDialogModule,
	],
})
export class CaWebviewerComponent implements OnInit, OnDestroy {
	private mediaBaseService = inject(MediaBaseService);
	private translate = inject(TranslateService);
	private treatmentBaseService = inject(TreatmentBaseService);
	private anamnesisService = inject(AnamnesisService);
	private store = inject<Store<TreatmentState>>(Store);
	private featureFlagsService = inject(FeatureFlagsService);
	private webviewerModificationService = inject(WebviewerModificationService);
	private notificationService = inject(NotificationService);
	private config = inject<AppConfig>(APP_CONFIG_TOKEN);
	private dialog = inject(MatDialog);

	protected readonly faCheckToSlot = faCheckToSlot;
	protected readonly faSignInAlt = faSignInAlt;
	protected readonly faSpinner = faSpinner;
	protected readonly faSync = faSync;
	protected readonly faTimes = faTimes;
	protected readonly faQuestionCircle = faQuestionCircle;

	readonly fillHeight = input(false);
	readonly webViewerJsonFileIdInput = input<string>(''); //is currently only set by the ca-viewer app (and empty if component is called by tm2 (embedded))
	webViewerJsonFileId: string = '';
	readonly webviewerModificationInput = input<WebviewerDocument>(null);

	readonly caWebViewer = viewChild<ElementRef>('caWebViewer');
	readonly caWebViewerAlternative = viewChild<ElementRef>('caWebViewerAlternative');

	private windowMessageEventHandler: (event: MessageEvent) => void = this.onIframeWindowMessage.bind(this);
	private webviewerInitialized$: ReplaySubject<WebViewerMessageInitialized> = new ReplaySubject(1);
	private webviewerAlternativeInitialized$: ReplaySubject<WebViewerMessageInitialized> = new ReplaySubject(1);
	protected webviewerLoadingFinished$: ReplaySubject<WebViewerMessageLoadingFinished> = new ReplaySubject(1);
	protected webviewerAlternativeLoadingFinished$: ReplaySubject<WebViewerMessageLoadingFinished> = new ReplaySubject(1);

	caWebViewerUrl: SafeResourceUrl;
	productName: string;
	alternativeOptionWebviewerFileId: string;
	alternativeOptionProductName: string;
	translateSubscription: Subscription;

	showCopyLinkButton: boolean = false;

	webViewerUrl: string;
	webViewerUrlAlternativeOption: string;
	webviewer: Webviewer = null;
	webviewerAlternative: Webviewer = null;
	webviewerModification: WebviewerDocument = null;
	webviewerAlternativeModification: WebviewerDocument = null;
	protected modificationModeEntered: boolean | null = null;
	protected modificationHasComments: boolean | null = null;
	protected modificationHasCommentsAlternativeOption: boolean | null = null;

	treatmentSubscription: Subscription;
	treatmentId: string;

	readonly webviewerSubmitModificationEvent = output<{webviewer: Webviewer, data: WebViewerMessageSubmitModification}>();
	readonly webviewerCancelledModificationEvent = output<{webviewer: Webviewer, data: any}>();

	ngOnInit() {
		// bypass security for this url
		this.caWebViewerUrl = this.treatmentBaseService.getCaWebViewerSource();

		// Add window message event listener
		window.addEventListener("message", this.windowMessageEventHandler);

		// subscribe language changes
		this.translateSubscription = this.translate.onLangChange
			.subscribe(e => {
				// normal web viewer
				if (this.caWebViewer()?.nativeElement) {
					this.caWebViewer().nativeElement.contentWindow
							.postMessage({language: e.lang}, CA_WEBVIEWER_APP_ORIGIN);
				}
				// alternative option web viewer
				if (this.caWebViewerAlternative()?.nativeElement) {
					this.caWebViewerAlternative().nativeElement.contentWindow
						.postMessage({language: e.lang}, CA_WEBVIEWER_APP_ORIGIN);
				}
			});

		this.webViewerJsonFileId = this.webViewerJsonFileIdInput();
		if (this.webViewerJsonFileId){
			//this path is reached, if the webviewer url is called as app via browser
			this.getFile(this.webViewerJsonFileId);
			if (
				this.featureFlagsService.isFeatureEnabled(FeatureFlag.ENABLE_WEBVIEWER_MODIFICATIONS) &&
				this.webviewerModificationInput()
			) {
				this.webviewerModification = this.webviewerModificationInput();
				this.webviewerLoadingFinished$.subscribe(() => {
					this.postModification(this.webviewerModification, false);
				});
			}
		} else {
			//this path is reached in the embedded version inside the tm treatment
			this.showCopyLinkButton = true;
			this.treatmentSubscription = this.store.pipe(
				select(selectTreatmentFromUrl),
				filter(treatment => !!treatment && treatment._id !== this.treatmentId),
			).subscribe((treatment: TreatmentModel) => {
				// Reset Webviewers.
				this.treatmentId = treatment._id;
				this.webviewerInitialized$ = new ReplaySubject(1);
				this.webviewerAlternativeInitialized$ = new ReplaySubject(1);
				this.webviewerLoadingFinished$ = new ReplaySubject(1);
				this.webviewerAlternativeLoadingFinished$ = new ReplaySubject(1);
				this.webViewerUrl = null;
				this.webViewerUrlAlternativeOption = null;
				this.webviewer = null;
				this.webviewerAlternative = null;
				this.alternativeOptionWebviewerFileId = null;
				this.webViewerJsonFileId = '';
				this.webviewerModification = null;
				this.webviewerAlternativeModification = null;

				// Update product names
				this.productName = ProductInfoForKey[treatment.product.key as ProductKey].name;
				if (treatment.upgrade) {
					this.alternativeOptionProductName = ProductInfoForKey[treatment.upgrade.productKey]?.name;
				}

				// Update webviewers urls.
				const anamnese = treatment.quotation?.anamnese;
				if (anamnese?.releaseData?.release) {
					const release = anamnese.releaseData.release;
					if (release.webViewerUrl) { // Requested option.
						this.webViewerUrl = release.webViewerUrl;
						this.webviewer = treatment.webviewers?.find(webviewer => webviewer.url === this.webViewerUrl);
						const urlParts = this.webViewerUrl.split('/');
						const isCaViewerLink = urlParts.some(part => part === 'ca-viewer');
						if (isCaViewerLink) {
							this.webViewerJsonFileId = urlParts.at(-1); // The media id of the .json file.
						}
					}
					if (release.webViewerUrlUpgrade) {
						// XXX Ignore "overruled" original planning backup link.
						// See the 'prepareTreatmentUpgrade()' function in treatment.service.ts for context.
						const isBackupLinkAfterAlternativeOptionRelease = release.webViewerUrlUpgrade.startsWith('(org)');
						if (!isBackupLinkAfterAlternativeOptionRelease) {
							this.webViewerUrlAlternativeOption = release.webViewerUrlUpgrade;
							this.webviewerAlternative = treatment.webviewers?.find(webviewer => webviewer.url === this.webViewerUrlAlternativeOption);
							const urlParts = this.webViewerUrlAlternativeOption.split('/');
							const isCaViewerLink = urlParts.some(part => part === 'ca-viewer');
							if (isCaViewerLink) {
								this.alternativeOptionWebviewerFileId = urlParts.at(-1);  // The media id of the .json file.
							}
						}
					}
	 				if (this.webViewerJsonFileId) {
						this.getFile(this.webViewerJsonFileId);
						this.fetchLatestRelatedModification();
					}
					if (this.alternativeOptionWebviewerFileId) {
						this.getFile(this.alternativeOptionWebviewerFileId, true);
						this.fetchLatestRelatedModification(true);
					}
				}

				if (this.isFunctionALTreatment(treatment)) {
					this.anamnesisService.getReleaseData(treatment._id)
						.pipe(
							take(1),
							filter(releaseData => !!releaseData)
						).subscribe({
							next: (releaseData: ReleaseData) => {
								if (releaseData.release?.webViewerUrl) {
									this.webViewerUrl = releaseData.release.webViewerUrl;
									this.webviewer = treatment.webviewers?.find(webviewer => webviewer.url === this.webViewerUrl);
									const urlParts = this.webViewerUrl.split('/');
									const isCaViewerLink = urlParts.some(part => part === 'ca-viewer');
									if (isCaViewerLink) {
										this.webViewerJsonFileId = urlParts.at(-1); // The media id of the .json file.
									}
								}

								if (this.webViewerJsonFileId) {
									this.getFile(this.webViewerJsonFileId);
									this.fetchLatestRelatedModification();
								}
							},
							error: (error) => {
								console.error("Error fetching treatment release data", error);
							}
						});
				}
			});
		}
	}

	ngOnDestroy(): void {
		this.translateSubscription.unsubscribe();
		this.treatmentSubscription?.unsubscribe();
		window.removeEventListener("message", this.windowMessageEventHandler);
	}

	private getFile(fileId: string, isAlternativeOption = false): void {
		const webViewerInput$ = this.mediaBaseService.getFile(fileId);
		const initialized$ = isAlternativeOption
			? this.webviewerAlternativeInitialized$
			: this.webviewerInitialized$;

		forkJoin([initialized$, webViewerInput$]).subscribe(([_, webViewerInput]) => {
			const element = isAlternativeOption ? this.caWebViewerAlternative() : this.caWebViewer();
			const contentWindow = element?.nativeElement?.contentWindow;
			if (contentWindow) {
				contentWindow.postMessage(
					{
						...webViewerInput,
						meta: {
							...(webViewerInput.meta ?? {}),
							mediaId: fileId,
						},
					},
					CA_WEBVIEWER_APP_ORIGIN
				);
				contentWindow.postMessage({ language: this.translate.getCurrentLang() }, CA_WEBVIEWER_APP_ORIGIN);
			}
		});
	}

	private fetchLatestRelatedModification(isAlternativeOption = false): void {
		if (!this.featureFlagsService.isFeatureEnabled(FeatureFlag.ENABLE_WEBVIEWER_MODIFICATIONS)) {
			return;
		}

		const webviewer = isAlternativeOption ? this.webviewerAlternative : this.webviewer;
		const loadingFinished$ = isAlternativeOption
			? this.webviewerAlternativeLoadingFinished$
			: this.webviewerLoadingFinished$;
		if (webviewer?.relatedModifications?.length) {
			forkJoin([
				this.webviewerModificationService.findOne(webviewer.relatedModifications.at(-1)),
				loadingFinished$,
			]).subscribe({
				next: ([modification, _]) => {
					if (!isAlternativeOption) {
						this.webviewerModification = modification;
					}
					else {
						this.webviewerAlternativeModification = modification;
					}
					this.postModification(modification, isAlternativeOption);
				},
				error: (error) => {
					this.notificationService.showErrorMessage(
						error.error?.detailedMessage ?? error.error?.message ?? error.message ?? error,
						null,
						10000
					);
				},
			});
		}
	}

	private postModification(
		modificationDocument: WebviewerDocument | null,
		isAlternativeOption = false
	): void {
		let payloadOrNull: WebViewerModificationDataContainer | null = null;
		if (modificationDocument && this.isWebViewerModificationDataContainer(modificationDocument)) {
			payloadOrNull = {
				...modificationDocument,
				meta: {
					...(modificationDocument.meta ?? {}),
					createdAt: modificationDocument.meta?.createdAt ?? modificationDocument.createdAt,
					entityId: modificationDocument.meta?.entityId ?? modificationDocument._id,
				},
			};
		}

		const message: WebViewerApiCommandLoadModification = {
			command: WebViewerApiCommand.LOAD_MODIFICATION,
			options: { append: false },
			payload: payloadOrNull,
		};
		const element = isAlternativeOption ? this.caWebViewerAlternative() : this.caWebViewer();
		const contentWindow = element?.nativeElement?.contentWindow;
		if (contentWindow) {
			contentWindow.postMessage(message, CA_WEBVIEWER_APP_ORIGIN);
		}
	}

	private isWebViewerModificationDataContainer(
		modificationDocument: WebviewerDocument
	): modificationDocument is WebviewerDocument & {
		type: WebViewerDataContainerType.MODIFICATION;
		data: WebViewerModificationData;
	} {
		return modificationDocument.type === WebViewerDataContainerType.MODIFICATION;
	}

	private onIframeWindowMessage(event: MessageEvent): void {
		if (!isWebviewerMessage(event)) {
			return;
		}
		event.stopPropagation();

		if (event.data.type === WebViewerMessageType.INITIALIZED) {
			if (event.source === this.caWebViewer()?.nativeElement?.contentWindow) {
				this.webviewerInitialized$.next(event.data);
				this.webviewerInitialized$.complete();
			} else if (event.source === this.caWebViewerAlternative()?.nativeElement?.contentWindow) {
				this.webviewerAlternativeInitialized$.next(event.data);
				this.webviewerAlternativeInitialized$.complete();
			}
			return;
		}

		if (event.data.type === WebViewerMessageType.LOADING_FINISHED) {
			if (event.source === this.caWebViewer()?.nativeElement?.contentWindow) {
				this.webviewerLoadingFinished$.next(event.data);
				this.webviewerLoadingFinished$.complete();
			} else if (event.source === this.caWebViewerAlternative()?.nativeElement?.contentWindow) {
				this.webviewerAlternativeLoadingFinished$.next(event.data);
				this.webviewerAlternativeLoadingFinished$.complete();
			}
			return;
		}

		if (event.data.type === WebViewerMessageType.CANCELLED_MODIFICATION) {
			const webviewer =
				event.source === this.caWebViewer()?.nativeElement?.contentWindow
					? this.webviewer
					: this.webviewerAlternative;
			this.modificationModeEntered = null;
			this.resetLastModification();
			this.webviewerCancelledModificationEvent.emit({ webviewer: webviewer, data: event.data });
			return;
		}

		if (event.data.type === WebViewerMessageType.SUBMIT_MODIFICATION) {
			this.modificationModeEntered = null;
			const webviewer =
				event.source === this.caWebViewer()?.nativeElement?.contentWindow
					? this.webviewer
					: this.webviewerAlternative;
			if ((event.source as Window)?.document?.fullscreenElement) {
				(event.source as Window)?.document.exitFullscreen();
			}
			this.webviewerSubmitModificationEvent.emit({ webviewer: webviewer, data: event.data });
			return;
		}

		if (
			event.data.type === WebViewerMessageType.MODIFICATION_CHANGED &&
			event.data.payload.type === "comments_changed"
		) {
			if (event.source === this.caWebViewer()?.nativeElement?.contentWindow) {
				this.modificationHasComments = event.data.payload.comments.length > 0;
			} else if (event.source === this.caWebViewerAlternative()?.nativeElement?.contentWindow) {
				this.modificationHasCommentsAlternativeOption = event.data.payload.comments.length > 0;
			}
			return;
		}
	}

	private isFunctionALTreatment(treatment: TreatmentModel): boolean {
		return FUNCTIONAL_PRODUCT_KEYS.includes(treatment.product.key);
	}

	/**
	 * Enters the modification mode for the webviewer.
	 * @param alternativeOption Whether to enter the modification mode for the alternative option webviewer (default: false).
	 * @param isNewModification Whether to create a new modification (default: true).
	 */
	public enterModificationMode(alternativeOption: boolean = false, isNewModification: boolean = true): void {
		this.modificationModeEntered = alternativeOption;
		const element = alternativeOption ? this.caWebViewerAlternative() : this.caWebViewer();
		const command: WebViewerApiCommandEnterModificationMode = {
			command: WebViewerApiCommand.ENTER_MODIFICATION_MODE,
			payload: {
				isNewModification: isNewModification,
				role:
					this.config.app === ClientName.TM_ADMIN
						? WebviewerCreatedByRole.TECHNICIAN
						: WebviewerCreatedByRole.DOCTOR,
				isSetupOnly: this.config.app !== ClientName.TM_ADMIN
			}
		};
		element.nativeElement.contentWindow.postMessage(command, CA_WEBVIEWER_APP_ORIGIN);
	}

	/**
	 * Trigger the modification submission for the webviewer.
	 * @param alternativeOption Whether to trigger the modification submission for the alternative option webviewer (default: false).
	 */
	protected requestSubmitModification(alternativeOption: boolean = false): void {
		const element = alternativeOption ? this.caWebViewerAlternative() : this.caWebViewer();
		const command: WebViewerApiCommandSubmitModification = {
			command: WebViewerApiCommand.SUBMIT_MODIFICATION,
		};
		element.nativeElement.contentWindow.postMessage(command, CA_WEBVIEWER_APP_ORIGIN);
	}

	/**
	 * Cancels the modification mode for the webviewer and resets the modification state (async).
	 * @param alternativeOption Whether to cancel the modification mode for the alternative option webviewer (default: false).
	 */
	protected requestCancelModification(alternativeOption: boolean = false): void {
		const element = alternativeOption ? this.caWebViewerAlternative() : this.caWebViewer();
		const command: WebViewerApiCommandCancelModification = {
			command: WebViewerApiCommand.CANCEL_MODIFICATION
		};
		element.nativeElement.contentWindow.postMessage(command, CA_WEBVIEWER_APP_ORIGIN);
	}

	/**
	 * Resets/reloads the last loaded modification, if any, or clears the modification state.
	 *
	 * Called from the treatment release component when correction is cancelled.
	 */
	public resetLastModification() {
		this.postModification(this.webviewerModification, false);
		this.postModification(this.webviewerAlternativeModification, true);
	}

	/**
	 * Scrolls the webviewer into view.
	 * @param alternativeOption Whether to scroll the alternative option webviewer (default: false).
	 */
	public scrollIntoView(alternativeOption: boolean = false): void {
		const element = alternativeOption ? this.caWebViewerAlternative() : this.caWebViewer();
		element.nativeElement.scrollIntoView({ behavior: "smooth", block: "center" });
	}

	/**
	 * Automatically opens the annotation help dialog if it is not set to not show again.
	 */
	public maybeAutoOpenAnnotationHelpDialog(): void {
		if (!WebviewerAnnotationHelpDialogComponent.shouldNotShowHelp()) {
			this.openAnnotationHelpDialog();
		}
	}

	protected openAnnotationHelpDialog(): void {
		WebviewerAnnotationHelpDialogComponent.openAnnotationHelpDialog(this.dialog);
	}

	/** Get the loading finished observable for the webviewer. */
	public getWebviewerLoadingFinished$(
		alternativeOption: boolean = false
	): Observable<WebViewerMessageLoadingFinished> {
		return alternativeOption ? this.webviewerAlternativeLoadingFinished$ : this.webviewerLoadingFinished$;
	}
}
